/* eslint @typescript-eslint/no-use-before-define: 0 */
import { Box, Button, TreeView } from 'devextreme-react';
import { Item } from 'devextreme-react/box';
import DropDownBox from 'devextreme-react/drop-down-box';
import notify from 'devextreme/ui/notify';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { deleteApi, getItemApi, getListApi, postApi, putApi } from '../../apiUtils';
import { useAuth } from '../../contexts/auth';
import { useLoading } from '../../contexts/loadingContext';
import { useMobileAuth } from '../../contexts/mobileAuthContext';
import { useTranslation } from '../../contexts/translation';
import { useClearButtonProps } from '../../hooks/useClearButtonProps';
import { getParameters, parseFilterParameters, setParameters } from '../../hooks/useField';
import { useSaveDataGridFilter } from '../../hooks/useSaveDataGridFilter';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import GeneralReport from '../../pages/Reports/General/GeneralReport';
import { GeneralReportSetting, GeneralReportSettingInput } from '../../types/reportSetting.types';
import { API_URL } from '../../utils/apiUrl';
import './ReportSelector.scss';

export enum FilterActionState {
  NEW = 'Filter.New',
  EDIT = 'Filter.Edit',
}

type ReportType = {
  id: number;
  key: string;
};
export interface Props {
  fields: Array<any>;
  dataGridRef: any;
  filterName: string;
  onClear: () => void;
  onFilterChange: (filter: string | null, datagrid: any | null) => void;
  report: GeneralReport;
  openDialog: (action: FilterActionState | null) => void;
  fetchData: (params: any) => void;
  isDefault?: boolean;
  setIsDefault: (value: boolean) => void;
  loadDefaultSetting: boolean;
  loadDefaultSettingStorageKey: string;
  paramPosition: number;
  platform: 'desktop' | 'mobile';
  datagridId: string;
}

type ReportSettingsParam = {
  settingId: string;
};

const GeneralReportFilterSelector = forwardRef(
  (
    {
      fields,
      filterName,
      dataGridRef,
      report,
      onClear,
      onFilterChange,
      openDialog,
      fetchData,
      isDefault = false,
      setIsDefault,
      loadDefaultSetting,
      loadDefaultSettingStorageKey,
      paramPosition,
      platform,
      datagridId,
    }: Props,
    ref,
  ) => {
    const { stopLoading } = useLoading();
    const [filters, setFilters] = useState<Array<GeneralReportSetting>>([]);
    const [filterId, setFilterId] = useState<any>(null);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [filterBoxOpened, setFilterBoxOpened] = useState<boolean>(false);
    const treeViewRef = useRef<any>(null);
    const { user: userMobile } = useMobileAuth();
    const { user: userDesktop } = useAuth();
    const user = platform === 'mobile' ? userMobile : userDesktop;
    const userId = user?.personalNumber ?? '';
    const { loadFilter, saveFilter, getActualState, getActualJsonState } = useSaveDataGridFilter(
      `general_reports_${report.id}`,
      dataGridRef,
    );
    const transFormName = 'Reports.ReportSelector';
    const { translate } = useTranslation();
    const clearButtonProps = useClearButtonProps();

    const { pathname } = useLocation();

    const pathParams = pathname.split('/');
    const settingId = pathParams.length > paramPosition ? pathParams[paramPosition] : null;

    const [filterSettingSession, setFilterSettingSession] = useSessionStorage(
      loadDefaultSettingStorageKey,
      {},
    );
    const [onceLoadedFromSession, setOnceLoadedFromSession] = useState(false);

    /**
     * Fetch všech filtrů daného typu z API pri mountu komponenty
     */
    useEffect(() => {
      if (filters) {
        handleDefaultFilterSelection(filters);
      }
    }, [settingId]);

    /**
     * Fetch všech filtrů daného typu z API pri mountu komponenty
     */
    useEffect(() => {
      getFavoriteFilters();
    }, []);

    const handleDefaultFilterSelection = (filters: Array<GeneralReportSetting>) => {
      const defaultFromUrl = filters.find((item) => item.id === Number(settingId));
      const defaultUser = filters.find((item) => item.isDefault && item.user === userId);
      const defaultGlobal = filters.find((item) => (!item.user || item.user === 0) && item.isDefault);
      
      // 05.01.2026 - issue #683
      // 1) první s IsDefault = true pro aktivního uživatele
      // 2) pokud není 1), tak první s IsDefault = true a User = NULL
      // V issue nebylo popsáno nastavení z URL
      let defaultFilter: any = null;
      if(defaultFromUrl)
        defaultFilter = defaultFromUrl;
      else if(defaultUser)
        defaultFilter = defaultUser;
      else
        defaultFilter = defaultGlobal;
      
      setSelectedFilter(defaultFilter);
      setFilterId(defaultFilter?.id ?? null);
    };

    /**
     * Slouží pro možnost volání funkcí childu z parenta
     */
    useImperativeHandle(ref, () => ({
      /**
       * Funkce pro smazání vybraného filtru
       */
      async deleteFilter() {
        await deleteApi({
          url: `${API_URL.GENERAL_REPORT_SETTING_DELETE}`,
          id: filterId,
          callAfterSuccess: () => {
            const newFilters: GeneralReportSetting[] = [...filters];
            const idx = newFilters.findIndex((item) => item.id === filterId);
            newFilters.splice(idx, 1);
            setFilters(newFilters);
            setFilterId(undefined);
            setSelectedFilter(null);
            openDialog(null);
            stopLoading();
          },
        });
      },

      /**
       * Funkce pro přidání nebo editaci filtru
       * @param actionState informace o tom jestli se jedná o přidání nového filtru nebo editaci stávajícího
       */
      async saveOrEditFilter(actionState: FilterActionState) {
        const filterParams = getParameters(fields.filter((item) => item.name !== null));
        const dataGridState =
          getActualState() !== null && JSON.parse(getActualJsonState(true) ?? '');
        const data: GeneralReportSettingInput = {
          reportId: report.id,
          name: filterName,
          user: user?.personalNumber ?? null,
          isDefault: isDefault!,
          setting: JSON.stringify({
            filter: filterParams,
            dataGrid: dataGridState,
          }),
        };

        if (actionState === FilterActionState.EDIT) {
          // edit API action
          const sendData = {
            ...data,
            id: filterId,
            isDefault: isDefault!,
          };

          const response = await putApi({
            url: API_URL.GENERAL_REPORT_SETTING_PUT,
            data: sendData,
            hideNotifications: true,
          });
          if (response !== null) {
            // selectDefault=false – nechceme, aby refetch přebral výběr přes
            // handleDefaultFilterSelection (to by dočasně přepnulo filterId na jiný
            // filtr / null a vyvolalo zbytečný/špatný fetch reportu). Výběr chceme
            // vždy ponechat na právě editovaném filtru.
            await getFavoriteFilters(false);
            setSelectedFilter(sendData.id);
            setFilterId(sendData.id);
            // Editovaný filtr byl už vybraný, takže se filterId nemění a useEffect na
            // [filterId] by se znovu nespustil – aby se report po uložení znovu načetl
            // s aktuálními parametry, zavoláme applyFilter (a tedy fetchData) explicitně.
            await applyFilter(sendData.id);
          }
        } else {
          // create API action
          const response = await postApi<GeneralReportSetting>({
            url: API_URL.GENERAL_REPORT_SETTING_POST,
            data,
            hideNotifications: true,
            callAfterError: (result) => {
              notify(
                translate!(
                  'Filter setting with this name already exists, select another.',
                  'Reports.ReportSelector',
                ),
                'error',
              );
            },
          });
          if (response) {
            const newFilters: GeneralReportSetting[] = [...filters, response];
            setFilters(newFilters);
            setSelectedFilter(response.id);
            setFilterId(response.id);
            openDialog(null);
          }
        }
      },
    }));

    /**
     * Funkce pro nastavené vybraného filtru do UI (fieldy + datagrid) při změně filtru
     */
    useEffect(() => {
      if (filterId !== -1) {
        applyFilter(filterId);
      }
    }, [filterId]);

    /**
     * Fetch všech filtrů daného typu z API
     */
    const getFavoriteFilters = async (selectDefault: boolean = true) => {
      await getListApi<Array<GeneralReportSetting>>({
        url: `${API_URL.GENERAL_REPORT_SETTING_LIST_FOR_USER}`,
        params: {
          userId,
          reportId: report.id,
        },
        callAfterSuccess: (result: Array<GeneralReportSetting>) => {
          setFilters(result);
          if (selectDefault) handleDefaultFilterSelection(result);
        },
      });
    };

    /**
     * Funkce pro fetch detailu vybraného filtru a nastavení parametrů pro fieldy a datagrid
     * @param filterId id filtru
     */
    const applyFilter = async (filterId) => {
      if (filterId) {
        await getItemApi<GeneralReportSetting>({
          url: API_URL.GENERAL_REPORT_SETTING_ITEM,
          params: {
            id: filterId.id ?? filterId,
          },
          callAfterSuccess: async (result) => {
            const filterSetting = JSON.parse(result.setting);
            const { filter, dataGrid } = filterSetting;

            const currentFilter =
              !loadDefaultSetting && !onceLoadedFromSession ? filterSettingSession : filter;
            onFilterChange(result.name, dataGrid);
            setParameters(fields, currentFilter);
            setFilterSettingSession(currentFilter);
            openDialog(null);
            fetchData(parseFilterParameters(currentFilter));
            setIsDefault(result.isDefault);
            if (!loadDefaultSetting) setOnceLoadedFromSession(true);

            if (onceLoadedFromSession) window.sessionStorage.removeItem(datagridId);
          },
        });
      }
    };

    /**
     * Funkce pro zavření dropdownboxu po vybrání položky
     */
    const onFilterBoxOpened = (e) => {
      if (e.name === 'opened') {
        setFilterBoxOpened(e.value);
      }
    };

    /**
     * Funkce pro výběr filtru ze seznamu
     *  + pokud je zaznamenáno kliknutí na "clear X", volá se funkce pro nastavení výchozích hodnot
     */
    const onFilterSelect = (e) => {
      setFilterBoxOpened(false);
      syncTreeViewSelection(e);
      if (!e.value) {
        onClear();
        onFilterChange(null, null);
      }
    };

    /**
     * Nastavení výběru v TreeView komponentě po výběru položky ze seznamu
     */
    const syncTreeViewSelection = (e) => {
      if (!treeViewRef) return;
      setFilterId(e.value);
      if (e.value) {
        treeViewRef?.current?.instance.selectItem(e.value);
      }
    };

    /**
     * Obslužná funkce pro vrácení vybraného filtru ze seznamu
     */
    const getSelectedFilter = (filterId: number) => {
      return filters.find((filter) => filter.id === filterId);
    };

    /**
     * Listener TreeView komponenty po změně výběru v seznamu
     */
    const treeViewItemSelectionChanged = (e) => {
      setSelectedFilter(e.itemData);
      setFilterId(e.itemData?.id);
    };

    const treeViewOnContentReady = (e) => {
      e.component.selectItem(filterId);
    };

    const renderItem = (itemData: GeneralReportSetting) => {
      const isGlobal = !itemData.user || itemData.user === 0;
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {isGlobal && <i className="dx-icon-globe icon" style={{ marginRight: 8 }}></i>}
          {!isGlobal && <i className="dx-icon-user icon" style={{ marginRight: 8 }}></i>}
          {itemData.name}
        </span>
      );
    };

    /**
     * Render TreeView komponenty pro DropDownBox
     */
    const treeViewRender = () => (
      <TreeView
        ref={treeViewRef}
        dataSource={filters}
        dataStructure="plain"
        selectionMode="single"
        keyExpr="id"
        selectByClick={true}
        searchEnabled={true}
        displayExpr="name"
        itemRender={renderItem}
        onContentReady={treeViewOnContentReady}
        onItemClick={() => setFilterBoxOpened(false)}
        onItemSelectionChanged={treeViewItemSelectionChanged}
      />
    );

    return (
      <>
        <Box direction="row" width="100%">
          <Item ratio={0} baseSize={platform === 'desktop' ? '20%' : '100%'}>
            <DropDownBox
              value={filterId}
              valueExpr="id"
              displayExpr="name"
              opened={filterBoxOpened}
              dataSource={filters}
              placeholder={translate!('Select a filter...', transFormName)}
              onValueChanged={onFilterSelect}
              onOptionChanged={onFilterBoxOpened}
              contentRender={treeViewRender}
              {...clearButtonProps}
            />
          </Item>
          {platform === 'desktop' && (
            <Item ratio={0} baseSize={100}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  style={{ marginLeft: 16 }}
                  text={translate!('Save as', transFormName)}
                  type="success"
                  onClick={() => openDialog(FilterActionState.NEW)}
                />
                <Button
                  style={{ marginLeft: 16 }}
                  text={translate!('Edit filter', transFormName)}
                  type="success"
                  onClick={() => openDialog(FilterActionState.EDIT)}
                  disabled={
                    !filterId ||
                    !getSelectedFilter(filterId)?.user ||
                    getSelectedFilter(filterId)?.user !== user?.personalNumber
                  }
                />
              </div>
            </Item>
          )}
        </Box>
      </>
    );
  },
);

GeneralReportFilterSelector.displayName = 'GeneralReportFilterSelector';

export default GeneralReportFilterSelector;
